"use client";

import { useState } from "react";
import Image from "next/image";
import { Check, Copy, Download } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import type { BuilderPreset } from "./logo-builder/types";
import type {
  ColorGroup,
  IconSample,
  LogoSymbol,
  LogoVariant,
  PrincipleItem,
  TypeSpec,
} from "./types";

/* ───────────────── Copy chip ───────────────── */

export function CopyChip({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy ${label ?? value}`}
      className={cn(
        "group border-border bg-surface-2/60 inline-flex items-center gap-1.5 rounded-md border",
        "text-muted-foreground px-2 py-1 font-mono text-[11px]",
        "hover:text-foreground hover:border-foreground/30 transition-colors"
      )}
    >
      <span className="tabular-nums">{label ?? value}</span>
      {copied ? (
        <Check size={12} className="text-brand-green" />
      ) : (
        <Copy size={12} className="opacity-60 group-hover:opacity-100" />
      )}
    </button>
  );
}

/* ───────────────── Section header ───────────────── */

export function GuidelineHeader({
  index,
  eyebrow,
  title,
  body,
}: {
  index: string;
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <div className="mb-8 sm:mb-10">
      <div className="mb-3 flex items-center gap-3">
        <span className="text-muted-foreground font-mono text-[11px] tracking-widest">{index}</span>
        <span className="bg-border h-px flex-1" />
        <span className="text-muted-foreground font-mono text-[10px] tracking-[0.2em] uppercase">
          {eyebrow}
        </span>
      </div>
      <h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
      <p className="text-muted-foreground mt-3 max-w-2xl text-sm leading-relaxed sm:text-base">
        {body}
      </p>
    </div>
  );
}

/* ───────────────── Logo card ───────────────── */

interface LogoCardProps {
  variant: LogoVariant;
  onDownload: (preset: BuilderPreset) => void;
}

export function LogoCard({ variant, onDownload }: LogoCardProps) {
  return (
    <div
      className="group border-border bg-card relative overflow-hidden rounded-2xl border"
      aria-label={variant.label}
    >
      <div
        className="flex aspect-[16/9] w-full items-center justify-center px-10 py-8"
        style={{ background: variant.bg }}
      >
        <div className="relative aspect-[400/90] w-full max-w-[220px]">
          <Image
            src={variant.src}
            alt={`Proxidize wordmark — ${variant.label}`}
            fill
            className={cn("object-contain", variant.invert && "invert")}
            sizes="220px"
          />
        </div>
      </div>
      <div className="border-border flex items-start justify-between gap-4 border-t px-5 py-4">
        <div className="min-w-0">
          <p className="text-foreground text-sm font-medium">{variant.label}</p>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            {variant.description}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onDownload(variant.builder)}
          className="border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors"
          aria-label={`Download ${variant.label} wordmark`}
        >
          <Download size={12} />
          Download
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Symbol / app icon ───────────────── */

interface SymbolCardProps {
  symbol: LogoSymbol;
  onDownload: (preset: BuilderPreset) => void;
}

export function SymbolCard({ symbol, onDownload }: SymbolCardProps) {
  return (
    <div
      className="group border-border bg-card relative overflow-hidden rounded-2xl border"
      aria-label={symbol.label}
    >
      <div className="bg-surface-2/40 flex aspect-[16/9] w-full items-center justify-center px-6 py-8">
        <div className="ring-foreground/10 relative h-24 w-24 shrink-0 overflow-hidden rounded-xl ring-1">
          <Image
            src={symbol.src}
            alt={`Proxidize symbol — ${symbol.label}`}
            fill
            className="object-contain"
            sizes="96px"
          />
        </div>
      </div>
      <div className="border-border flex items-start justify-between gap-4 border-t px-5 py-4">
        <div className="min-w-0">
          <p className="text-foreground text-sm font-medium">{symbol.label}</p>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            {symbol.description}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onDownload(symbol.builder)}
          className="border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors"
          aria-label={`Download ${symbol.label}`}
        >
          <Download size={12} />
          Download
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Logo Do / Don't ───────────────── */

export function LogoRule({
  tone,
  title,
  items,
}: {
  tone: "do" | "dont";
  title: string;
  items: readonly string[];
}) {
  const isDo = tone === "do";
  const color = isDo ? "#5b8cff" : "#FF6B6B";
  return (
    <div className="border-border bg-card rounded-2xl border p-6">
      <div className="mb-4 flex items-center gap-2">
        <span
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold"
          style={{
            background: `${color}1F`,
            color,
          }}
        >
          {isDo ? "✓" : "✕"}
        </span>
        <h3 className="text-foreground text-lg font-semibold">{title}</h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item} className="text-muted-foreground flex gap-2 text-sm leading-relaxed">
            <span
              className="mt-2 h-1 w-1 shrink-0 rounded-full"
              style={{ background: color }}
              aria-hidden
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────────────── Color swatch ───────────────── */

export function Swatch({
  name,
  token,
  hex,
  description,
  foreground,
}: {
  name: string;
  token: string;
  hex: string;
  description: string;
  foreground: "light" | "dark";
}) {
  const text = foreground === "light" ? "#F7F7F8" : "#0A0A0B";

  return (
    <div className="group border-border relative overflow-hidden rounded-2xl border">
      <div
        className="flex aspect-[5/4] flex-col justify-between p-5"
        style={{ background: hex, color: text }}
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase opacity-80">
            {token}
          </span>
        </div>
        <div>
          <p className="text-base leading-none font-semibold">{name}</p>
          <p className="mt-1 text-[11px] leading-relaxed opacity-70">{description}</p>
        </div>
      </div>
      <div className="bg-card border-border flex items-center justify-between gap-3 border-t px-4 py-3">
        <span className="text-muted-foreground font-mono text-xs">{hex}</span>
        <CopyChip value={hex} label="Copy" />
      </div>
    </div>
  );
}

/* ───────────────── Color group ───────────────── */

export function ColorGroupBlock({ group }: { group: ColorGroup }) {
  return (
    <div className="mb-12 last:mb-0">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h3 className="text-foreground text-lg font-semibold">{group.label}</h3>
        <p className="text-muted-foreground max-w-md text-right text-xs">{group.caption}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
        {group.swatches.map((s) => (
          <Swatch key={s.token} {...s} />
        ))}
      </div>
    </div>
  );
}

/* ───────────────── Type specimen ───────────────── */

export function TypeRow({ spec }: { spec: TypeSpec }) {
  return (
    <div className="border-border bg-card overflow-hidden rounded-2xl border">
      <div className="px-6 py-7 sm:px-8 sm:py-10">
        <p
          className={cn(spec.className, "text-foreground")}
          style={spec.role === "Mono" ? { fontFamily: "var(--font-mono)" } : undefined}
        >
          {spec.sample}
        </p>
      </div>
      {/* spec strip stays inside the specimen card, so its cells sit a step
          above the card surface rather than carrying their own borders */}
      <div className="border-border grid grid-cols-2 gap-3 border-t p-4 sm:grid-cols-5">
        <SpecCell label="Role" value={spec.role} />
        <SpecCell label="Family" value={spec.family} />
        <SpecCell label="Weight" value={spec.weight} />
        <SpecCell label="Size" value={spec.size} />
        <SpecCell label="Tracking" value={spec.tracking} />
      </div>
    </div>
  );
}

function SpecCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface-2 rounded-lg px-4 py-3">
      <p className="text-muted-foreground text-[10px] tracking-[0.18em] uppercase">{label}</p>
      <p className="text-foreground mt-1 text-xs font-medium tabular-nums">{value}</p>
    </div>
  );
}

/* ───────────────── Icon grid ───────────────── */

export function IconTile({ sample }: { sample: IconSample }) {
  const { Icon, name } = sample;
  return (
    <div className="group border-border bg-card hover:border-foreground/30 flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors">
      <div className="bg-surface-2/60 text-foreground group-hover:text-brand-yellow flex h-12 w-12 items-center justify-center rounded-lg transition-colors">
        <Icon size={20} strokeWidth={1.6} />
      </div>
      <span className="text-muted-foreground font-mono text-[10px] tracking-tight">{name}</span>
    </div>
  );
}

/* ───────────────── Principle card ───────────────── */

export function PrincipleCard({
  item,
  index,
  accent = true,
}: {
  item: PrincipleItem;
  index: number;
  accent?: boolean;
}) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="border-border bg-card relative overflow-hidden rounded-2xl border p-6"
    >
      {accent && (
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.5) 40%, rgba(91,140,255,0.5) 60%, transparent 100%)",
          }}
          aria-hidden
        />
      )}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-muted-foreground font-mono text-[10px]">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="bg-border h-px w-6" />
      </div>
      <h3 className="text-foreground text-lg font-semibold">{item.title}</h3>
      <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{item.body}</p>
    </motion.div>
  );
}
